import type { ChangeEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { CruiseDurationOptions } from 'components/Cruises/SearchPage/Filters/Inputs/CruiseWhenSelect/CruiseWhenSelectHowLong'
import { CRUISE_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useCruiseDurationLabels from 'hooks/Cruise/useCruiseDurationLabels'

const CruiseDurationFacetSlugByRange: Partial<Record<CRUISE_FLEXIBLE_DURATION_RANGE, string>> = {
  [CRUISE_FLEXIBLE_DURATION_RANGE.SHORT_STAY]: 'short',
  [CRUISE_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY]: 'medium',
  [CRUISE_FLEXIBLE_DURATION_RANGE.LONG_STAY]: 'long',
}

interface Props {
  durationFacets: Array<App.CruiseSearchFacetV2Item>
  onChange: (durationRange: Array<CRUISE_FLEXIBLE_DURATION_RANGE>) => void
}

function CruiseDurationFilter({
  durationFacets,
  onChange,
}: Props) {
  const durationLabels = useCruiseDurationLabels()
  const globalSearchState = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const { durationRange } = globalSearchState

  const selectedDurations = useMemo(() => new Set(durationRange.filter(Boolean)), [durationRange])

  const durationItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    const countBySlug = durationFacets.reduce<Record<string, number>>((acc, facet) => {
      acc[facet.slug] = facet.count
      return acc
    }, {})

    return CruiseDurationOptions.slice(1).map((option) => {
      const facetSlug = CruiseDurationFacetSlugByRange[option.duration]
      const count = facetSlug ? countBySlug[facetSlug] ?? 0 : 0

      return {
        value: option.duration,
        label: durationLabels[option.value] ?? option.value,
        count,
        disabled: count === 0,
      }
    })
  }, [durationFacets, durationLabels])
  const hasDurationItems = useMemo(() => {
    return durationItems.some(item => item.count != null && item.count > 0)
  }, [durationItems])

  const onDurationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const nextDuration = e.target.value as CRUISE_FLEXIBLE_DURATION_RANGE
    const durationRangeWithoutAny = durationRange.filter(Boolean) as Array<CRUISE_FLEXIBLE_DURATION_RANGE>
    const nextDurationRange = e.target.checked ?
        [...durationRangeWithoutAny, nextDuration] :
        durationRangeWithoutAny.filter(duration => duration !== nextDuration)

    searchDispatch({
      type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE,
      durationRange: nextDurationRange,
    })

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: [{
        label: nextDuration,
        type: 'duration_range',
        value: e.target.checked ? 'selected' : 'unselected',
      }],
    }))

    onChange(nextDurationRange)
  }, [durationRange, globalSearchState, onChange, searchDispatch])

  if (!hasDurationItems) {
    return null
  }

  return <div data-testid="cruise-duration-filter">
    <FilterPanelCheckboxGroup
      name="durationRange"
      items={durationItems}
      onChange={onDurationChange}
      selected={selectedDurations}
    />
  </div>
}

export default CruiseDurationFilter
